import { useTranslateForm } from 'hooks/use-translate-payoff';
import { LockableAmountFieldMetadata, LockablePercentFieldMetadata } from 'models/forms/fields';
import { useCallback } from 'react';
import { ErrorWrapper } from 'routes/essential-workspace/components/form/error-wrapper';
import { DEFAULT_INCREMENT_STEP } from 'routes/essential-workspace/components/form/fields/digit';
import { useFormErrors } from 'routes/essential-workspace/components/form/use-form-errors';

import { DigitInputWithLock } from './digit-input-with-lock';
import { useLockedDigitField } from './use-locked-digit-field';

type LockableDigitFieldProps = LockablePercentFieldMetadata | LockableAmountFieldMetadata;

export function LockableDigitFieldEssential(props: LockableDigitFieldProps) {
  const { id, translationKey, nbDecimals, decimalPadding, isReadOnly, type, isLocked } = props;
  const { value, setFormData, showValue, handleLockChanged } = useLockedDigitField(props);

  const { getFieldError } = useFormErrors();
  const fieldError = getFieldError(id);

  const { translateLabel } = useTranslateForm();
  const label = translateLabel(translationKey ?? id);

  const handleOnValueChanged = useCallback(
    (newValue: Nullable<number>) => {
      if (isLocked) {
        return;
      }
      if (value !== newValue) {
        setFormData({ value: newValue, customized: true });
      }
    },
    [setFormData, value, isLocked],
  );

  const handleAddOrRemove = useCallback(
    (action: 'add' | 'remove') => {
      if (isLocked) {
        return;
      }
      const newValue = action === 'add' ? (value ?? 0) + DEFAULT_INCREMENT_STEP : (value ?? 0) - DEFAULT_INCREMENT_STEP;
      setFormData({ value: newValue, customized: true });
    },
    [value, setFormData, isLocked],
  );

  const displayValue = showValue ? value : null;
  const unit = type === 'LockableAmountField' ? props.currency : '%';

  return (
    <ErrorWrapper error={fieldError}>
      <DigitInputWithLock
        id={id}
        label={label}
        unit={unit}
        value={displayValue}
        precision={nbDecimals}
        onValueChanged={handleOnValueChanged}
        onAddOrRemove={handleAddOrRemove}
        decimalPadding={decimalPadding}
        isReadOnly={isReadOnly}
        onLockChanged={handleLockChanged}
        isLocked={isLocked}
        isInvalid={fieldError?.severity === 'Error'}
        highlightColor={fieldError?.severity === 'Warning' ? 'info' : undefined}
        isDelegated={false}
        isDisabled={false}
        isLoading={false}
        tabIndex={0}
        incrementStep={isReadOnly ? undefined : DEFAULT_INCREMENT_STEP}
      />
    </ErrorWrapper>
  );
}
